import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Search,
  Plus,
  RefreshCw,
  LayoutGrid,
  List,
  Filter,
  Download,
  Upload,
  Star,
  Building2,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
} from 'lucide-react';
import Layout from '../components/Layout';
import SupplierCard from '../components/SupplierCard';
import SupplierFormDialog from '../components/SupplierFormDialog';
import purchaseService from '../services/purchaseService';

const SuppliersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // View and pagination state
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 12,
    total: 0,
    totalPages: 1
  });

  // Sorting and filtering
  const [sortBy, setSortBy] = useState('supplier_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filters, setFilters] = useState({
    search: '',
    supplier_type: '',
    is_active: '',
    is_preferred: '',
    rating: '',
    categories: []
  });

  // Dialog states
  const [supplierDialog, setSupplierDialog] = useState({
    open: false,
    mode: 'create',
    data: null
  });

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    supplier: null
  });

  const tabs = [
    { value: 'all', label: '全部供应商', filter: {} },
    { value: 'active', label: '启用中', filter: { is_active: true } },
    { value: 'preferred', label: '优选供应商', filter: { is_preferred: true } }
  ];

  const getTabIndex = (value) => tabs.findIndex(t => t.value === value);

  useEffect(() => {
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, sortBy, sortOrder, filters, activeTab]);

  useEffect(() => {
    if (location.state?.editSupplier) {
      setSupplierDialog({
        open: true,
        mode: 'edit',
        data: location.state.editSupplier
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const tabIndex = getTabIndex(activeTab);
      const combinedFilters = {
        ...filters,
        ...tabs[tabIndex].filter
      };

      const params = {
        page: pagination.page,
        limit: pagination.pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...combinedFilters
      };

      const response = await purchaseService.getSuppliers(params.page, params.limit, params);

      if (Array.isArray(response)) {
        setSuppliers(response);
        setPagination(prev => ({
          ...prev,
          total: response.length,
          totalPages: Math.ceil(response.length / prev.pageSize)
        }));
      } else {
        setSuppliers(response.suppliers || []);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0,
          totalPages: response.total_pages || 1
        }));
      }

    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError('Failed to load suppliers');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize, sortBy, sortOrder, filters, activeTab]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSupplierDialog = (mode, supplier = null) => {
    setSupplierDialog({
      open: true,
      mode,
      data: supplier
    });
  };

  const handleSupplierSubmit = async (supplierData) => {
    try {
      if (supplierDialog.mode === 'create') {
        await purchaseService.createSupplier(supplierData);
      } else if (supplierDialog.mode === 'edit') {
        await purchaseService.updateSupplier(supplierDialog.data._id, supplierData);
      }

      await loadSuppliers();
      setSupplierDialog({ open: false, mode: 'create', data: null });

    } catch (error) {
      console.error('Error saving supplier:', error);
      throw error;
    }
  };

  const handleDeleteSupplier = async () => {
    try {
      await purchaseService.deleteSupplier(deleteDialog.supplier._id);
      await loadSuppliers();
      setDeleteDialog({ open: false, supplier: null });
    } catch (error) {
      console.error('Error deleting supplier:', error);
      setError('Failed to delete supplier');
    }
  };

  const handleMenuAction = (action, supplier) => {
    switch (action) {
      case 'view':
        if (supplier && supplier._id) {
          navigate(`/suppliers/${String(supplier._id)}`);
        }
        break;
      case 'edit':
        handleSupplierDialog('edit', supplier);
        break;
      case 'delete':
        setDeleteDialog({ open: true, supplier });
        break;
      default:
        break;
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      supplier_type: '',
      is_active: '',
      is_preferred: '',
      rating: '',
      categories: []
    });
  };

  const handleExport = () => {
    console.log('Exporting suppliers...');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      notation: amount > 100000 ? 'compact' : 'standard'
    }).format(amount || 0);
  };

  const getSupplierTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'manufacturer': return '制造商';
      case 'distributor': return '分销商';
      case 'service': return '服务商';
      default: return '其他';
    }
  };

  const renderGridView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {suppliers.map((supplier) => (
        <SupplierCard
          key={supplier._id}
          supplier={supplier}
          size="medium"
          onEdit={(s) => handleSupplierDialog('edit', s)}
          onView={(s) => {
            if (s && s._id) {
              navigate(`/suppliers/${String(s._id)}`);
            }
          }}
          onDelete={(s) => setDeleteDialog({ open: true, supplier: s })}
          onClick={(s) => navigate(`/suppliers/${String(s._id)}`)}
        />
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-muted/50 border-b">
            <th className="px-4 py-3 text-left">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 -ml-2 font-semibold"
                onClick={() => handleSort('supplier_name')}
              >
                供应商名称
                <ArrowUpDown className="ml-1 h-4 w-4" />
              </Button>
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold">类型</th>
            <th className="px-4 py-3 text-left text-sm font-semibold">联系人</th>
            <th className="px-4 py-3 text-left text-sm font-semibold">联系方式</th>
            <th className="px-4 py-3 text-left text-sm font-semibold">地区</th>
            <th className="px-4 py-3 text-center text-sm font-semibold">评级</th>
            <th className="px-4 py-3 text-center text-sm font-semibold">状态</th>
            <th className="px-4 py-3 text-right text-sm font-semibold">信用额度</th>
            <th className="px-4 py-3 text-center text-sm font-semibold">操作</th>
          </tr>
        </thead>
        <tbody>
          {suppliers.map((supplier) => (
            <tr
              key={supplier._id}
              className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
              onClick={() => navigate(`/suppliers/${String(supplier._id)}`)}
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium text-sm">{supplier.supplier_name}</p>
                    <p className="text-xs text-muted-foreground">{supplier.supplier_code}</p>
                  </div>
                  {supplier.is_preferred && (
                    <Star className="h-4 w-4 text-warning fill-warning" />
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <Badge variant="outline" className="text-xs">
                  {getSupplierTypeLabel(supplier.supplier_type)}
                </Badge>
              </td>
              <td className="px-4 py-3 text-sm">{supplier.contact_person}</td>
              <td className="px-4 py-3">
                <p className="text-sm">{supplier.phone}</p>
                <p className="text-xs text-muted-foreground">{supplier.email}</p>
              </td>
              <td className="px-4 py-3 text-sm">
                {supplier.city}, {supplier.province}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={cn(
                        "h-3 w-3",
                        star <= (supplier.rating || 0)
                          ? "text-warning fill-warning"
                          : "text-muted-foreground/30"
                      )}
                    />
                  ))}
                </div>
              </td>
              <td className="px-4 py-3 text-center">
                <Badge
                  className={cn(
                    "text-xs",
                    supplier.is_active
                      ? "bg-success/20 text-success hover:bg-success/30"
                      : "bg-destructive/20 text-destructive hover:bg-destructive/30"
                  )}
                >
                  {supplier.is_active ? '启用' : '停用'}
                </Badge>
              </td>
              <td className="px-4 py-3 text-right text-sm">
                {formatCurrency(supplier.credit_limit)}
              </td>
              <td className="px-4 py-3 text-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleMenuAction('view', supplier); }}>
                      <Eye className="h-4 w-4 mr-2" />
                      查看详情
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleMenuAction('edit', supplier); }}>
                      <Pencil className="h-4 w-4 mr-2" />
                      编辑供应商
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleMenuAction('delete', supplier); }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      删除供应商
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout title="供应商管理" activeTab="suppliers">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              供应商管理
            </h1>
            <p className="text-muted-foreground">
              管理供应商信息、评估绩效和维护合作关系
            </p>
          </div>

          <div className="flex gap-2">
            <Button variant="outline">
              <Upload className="h-4 w-4 mr-2" />
              导入
            </Button>
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              导出
            </Button>
            <Button onClick={() => handleSupplierDialog('create')}>
              <Plus className="h-4 w-4 mr-2" />
              新建供应商
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
          <TabsList>
            {tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Filters and Controls */}
        <Card className="border bg-card mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative min-w-[200px] flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜索供应商..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-9"
                />
              </div>

              <Select
                value={filters.supplier_type || 'all'}
                onValueChange={(value) => handleFilterChange('supplier_type', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="供应商类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部类型</SelectItem>
                  <SelectItem value="manufacturer">制造商</SelectItem>
                  <SelectItem value="distributor">分销商</SelectItem>
                  <SelectItem value="service">服务商</SelectItem>
                  <SelectItem value="other">其他</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={filters.rating || 'all'}
                onValueChange={(value) => handleFilterChange('rating', value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="评级" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部评级</SelectItem>
                  <SelectItem value="5">5星</SelectItem>
                  <SelectItem value="4">4星以上</SelectItem>
                  <SelectItem value="3">3星以上</SelectItem>
                  <SelectItem value="2">2星以上</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex gap-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={clearFilters}>
                        <Filter className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>清空筛选</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={loadSuppliers}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>刷新数据</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="flex-1" />

              <div className="flex gap-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === 'grid' ? 'default' : 'ghost'}
                        size="icon"
                        onClick={() => setViewMode('grid')}
                      >
                        <LayoutGrid className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>网格视图</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={viewMode === 'list' ? 'default' : 'ghost'}
                        size="icon"
                        onClick={() => setViewMode('list')}
                      >
                        <List className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>列表视图</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-8">
            <p className="text-muted-foreground">加载中...</p>
          </div>
        ) : suppliers.length === 0 ? (
          <Card className="border bg-card">
            <CardContent className="py-12 text-center">
              <Building2 className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
              <h2 className="text-xl font-semibold text-muted-foreground mb-2">
                暂无供应商数据
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                开始添加供应商来管理您的供应链
              </p>
              <Button onClick={() => handleSupplierDialog('create')}>
                <Plus className="h-4 w-4 mr-2" />
                添加第一个供应商
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div>
            {viewMode === 'grid' ? renderGridView() : renderListView()}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }

                  return (
                    <Button
                      key={pageNum}
                      variant={pagination.page === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                      className="min-w-[36px]"
                    >
                      {pageNum}
                    </Button>
                  );
                })}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Dialogs */}
        <SupplierFormDialog
          open={supplierDialog.open}
          onClose={() => setSupplierDialog({ open: false, mode: 'create', data: null })}
          onSubmit={handleSupplierSubmit}
          initialData={supplierDialog.data}
          mode={supplierDialog.mode}
        />

        <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, supplier: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>确认删除供应商</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p>确定要删除供应商 "{deleteDialog.supplier?.supplier_name}" 吗？</p>
              <p className="text-sm text-muted-foreground mt-2">
                此操作不可撤销，相关的采购记录将保留但供应商信息将被清除。
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, supplier: null })}>
                取消
              </Button>
              <Button variant="destructive" onClick={handleDeleteSupplier}>
                确认删除
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SuppliersPage;
